import type {Instant, UUID} from './common'
/* eslint-disable i18next/no-literal-string -- const definitions are the source of truth */

export const ApprovalConditionField = {
    RISK_LEVEL: 'RISK_LEVEL',
    RESOURCE_TYPE: 'RESOURCE_TYPE',
    DATA_CLASSIFICATION: 'DATA_CLASSIFICATION',
    FUNCTION_GROUP_TYPE: 'FUNCTION_GROUP_TYPE',
    OWNER_PARTY: 'OWNER_PARTY',
} as const;
export type ApprovalConditionField = typeof ApprovalConditionField[keyof typeof ApprovalConditionField];

export const ConditionOperator = {
    EQUALS: 'EQUALS',
    NOT_EQUALS: 'NOT_EQUALS',
    IN: 'IN',
} as const;
export type ConditionOperator = typeof ConditionOperator[keyof typeof ConditionOperator];

export const ApprovalPolicyType = {
    ACCESS_PROFILE: 'ACCESS_PROFILE',
    FUNCTION_GROUP: 'FUNCTION_GROUP',
    AUTO_ASSIGN_RULE: 'AUTO_ASSIGN_RULE',
    RESTRICTION_RULE: 'RESTRICTION_RULE',
} as const;
export type ApprovalPolicyType = typeof ApprovalPolicyType[keyof typeof ApprovalPolicyType];

export const ResolverType = {
    RESOURCE_OWNER: 'RESOURCE_OWNER',
    PROFILE_OWNER: 'PROFILE_OWNER',
    MANAGER: 'MANAGER',
    SPECIFIC_IDENTITY: 'SPECIFIC_IDENTITY',
    TEAM_MEMBER: 'TEAM_MEMBER',
    FUNCTION_GROUP_OWNER: 'FUNCTION_GROUP_OWNER',
    RULE_OWNER: 'RULE_OWNER',
} as const;
export type ResolverType = typeof ResolverType[keyof typeof ResolverType];

export const ApprovalStrategy = {
    ANY: 'ANY',
    ALL: 'ALL',
} as const;
export type ApprovalStrategy = typeof ApprovalStrategy[keyof typeof ApprovalStrategy];

export interface ApprovalPolicyCondition {
    id: UUID;
    field: ApprovalConditionField;
    operator: ConditionOperator;
    value: string;
}

export interface ApprovalPolicyStep {
    id: UUID;
    stepOrder: number;
    resolverType: ResolverType;
    resolverParam: string | null;
    approvalStrategy: ApprovalStrategy;
    required: boolean;
}

export interface ApprovalPolicy {
    id: UUID;
    name: string;
    description: string | null;
    policyType: ApprovalPolicyType;
    priority: number;
    active: boolean;
    validFrom: Instant | null;
    validTo: Instant | null;
    isDefault: boolean;
    allowProxyApproval: boolean;
    conditions: ApprovalPolicyCondition[];
    steps: ApprovalPolicyStep[];
    version: number;
    createdAt: Instant;
    updatedAt: Instant;
}

export interface ApprovalPolicyConditionRequest {
    field: ApprovalConditionField;
    operator: ConditionOperator;
    value: string;
}

export interface ApprovalPolicyStepRequest {
    stepOrder: number;
    resolverType: ResolverType;
    resolverParam?: string;
    approvalStrategy: ApprovalStrategy;
    required: boolean;
}

export interface CreateApprovalPolicyRequest {
    name: string;
    description?: string;
    policyType: ApprovalPolicyType;
    priority: number;
    active: boolean;
    validFrom?: Instant;
    validTo?: Instant;
    allowProxyApproval: boolean;
    conditions: ApprovalPolicyConditionRequest[];
    steps: ApprovalPolicyStepRequest[];
}

export interface UpdateApprovalPolicyRequest {
    name: string;
    description?: string;
    policyType: ApprovalPolicyType;
    priority: number;
    active: boolean;
    validFrom?: Instant;
    validTo?: Instant;
    allowProxyApproval: boolean;
    conditions: ApprovalPolicyConditionRequest[];
    steps: ApprovalPolicyStepRequest[];
    version: number;
}

/* eslint-disable i18next/no-literal-string -- field name constants for backend API */
export const ApprovalPolicyResponseFields = {
    id: 'id',
    name: 'name',
    description: 'description',
    policyType: 'policyType',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    isDefault: 'isDefault',
    allowProxyApproval: 'allowProxyApproval',
    conditions: 'conditions',
    steps: 'steps',
    version: 'version',
    createdAt: 'createdAt',
    updatedAt: 'updatedAt',
} as const

export const CreateApprovalPolicyRequestFields = {
    name: 'name',
    description: 'description',
    policyType: 'policyType',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    allowProxyApproval: 'allowProxyApproval',
    conditions: 'conditions',
    steps: 'steps',
} as const

export const UpdateApprovalPolicyRequestFields = {
    name: 'name',
    description: 'description',
    policyType: 'policyType',
    priority: 'priority',
    active: 'active',
    validFrom: 'validFrom',
    validTo: 'validTo',
    allowProxyApproval: 'allowProxyApproval',
    conditions: 'conditions',
    steps: 'steps',
    version: 'version',
} as const
/* eslint-enable i18next/no-literal-string */
